import { createTheme } from '@mui/material';
import { ptBR } from '@mui/x-date-pickers/locales';

const ink = '#0B0A08';
const gold = '#C8A459';
const goldDark = '#9E7C38';
const goldSoft = '#F8F1DE';

const elevation = ['none'];
for (let i = 1; i < 25; i += 1) {
    const y = Math.max(1, Math.round(i * 0.45));
    const blur = Math.max(2, Math.round(i * 1.15));
    const alpha = (0.035 + i * 0.004).toFixed(3);
    elevation.push(`0px ${y}px ${blur}px rgba(11, 10, 8, ${alpha})`);
}

let theme = createTheme(
    {
        typography: {
            fontFamily: ['Inter', 'sans-serif'].join(','),
        },
        palette: {
            primary: {
                main: ink,
                light: '#383434',
                contrastText: '#FFFFFF',
            },
            secondary: {
                main: gold,
                dark: goldDark,
                light: goldSoft,
                contrastText: '#FFFFFF',
            },
            text: {
                primary: ink,
            },
        },
        shape: {
            borderRadius: 8,
        },
        shadows: elevation,
    },
    ptBR
);

theme = createTheme(theme, {
    typography: {
        h1: {
            fontWeight: 400,
            fontSize: '2rem',
        },
        h2: {
            fontWeight: 400,
            fontSize: '1.875rem',
        },
        h3: {
            fontWeight: 400,
            fontSize: '1.5rem',
        },
        subtitle1: {
            fontSize: '1rem',
            color: 'white',
            fontWeight: 400,
            [theme.breakpoints.up('lg')]: {
                fontSize: '1.25rem',
            },
        },
        subtitle2: {
            fontSize: '0.875rem',
            fontWeight: 400,
        },
        body1: {
            fontSize: '1rem',
            textAlign: 'center',
        },
        button: {
            fontWeight: 600,
            fontSize: '1rem',
            textTransform: 'none',
            textAlign: 'center'
        },
    },
    components: {
        MuiLink: {
            styleOverrides: {
                root: {
                    textDecoration: 'none',
                    transition: '.3s',
                    '&:hover': {
                        color: theme.palette.secondary.main,
                    }
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    minHeight: 40,
                    paddingLeft: 16,
                    paddingRight: 16,
                    letterSpacing: '0.01em',
                    boxShadow: 'none',
                    transition: 'background-color .2s ease, border-color .2s ease, color .2s ease',
                    '&:focus-visible': {
                        outline: `2px solid ${gold}`,
                        outlineOffset: 2,
                    },
                    '&.Mui-disabled': {
                        opacity: 0.5,
                    },
                },
                contained: {
                    boxShadow: 'none',
                    '&:hover': {
                        boxShadow: 'none',
                    },
                    '&:active': {
                        boxShadow: 'none',
                    },
                },
                containedPrimary: {
                    backgroundColor: ink,
                    color: '#FFFFFF',
                    '&:hover': {
                        backgroundColor: '#383434',
                    },
                    '&:active': {
                        backgroundColor: ink,
                    },
                },
                containedSecondary: {
                    backgroundColor: gold,
                    color: '#FFFFFF',
                    '&:hover': {
                        backgroundColor: goldDark,
                    },
                    '&:active': {
                        backgroundColor: '#8A6C30',
                    },
                    '&.Mui-disabled': {
                        backgroundColor: gold,
                        color: '#FFFFFF',
                    },
                },
                outlined: {
                    borderWidth: 1,
                    '&:hover': {
                        borderWidth: 1,
                    },
                },
                outlinedPrimary: {
                    color: ink,
                    borderColor: '#E5E2DA',
                    '&:hover': {
                        borderColor: ink,
                        backgroundColor: 'rgba(11,10,8,0.04)',
                    },
                },
                outlinedSecondary: {
                    color: goldDark,
                    borderColor: gold,
                    '&:hover': {
                        borderColor: goldDark,
                        backgroundColor: 'rgba(200,164,89,0.08)',
                    },
                },
                text: {
                    minHeight: 36,
                },
                textPrimary: {
                    color: ink,
                    '&:hover': {
                        backgroundColor: 'rgba(11,10,8,0.04)',
                    },
                },
                textSecondary: {
                    color: goldDark,
                    '&:hover': {
                        backgroundColor: 'rgba(200,164,89,0.08)',
                    },
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    '&:hover': {
                        backgroundColor: 'rgba(200,164,89,0.12)',
                    },
                    '&:focus-visible': {
                        outline: `2px solid ${gold}`,
                        outlineOffset: 2,
                    },
                },
            },
        },
        MuiButtonBase: {
            defaultProps: {
                disableRipple: false,
            },
        },
    },
});

export default theme;